import { serialByteEvent } from './channel.js';
import { SERIAL_COMMAND_DONE, SERIAL_TERMINAL } from './types.js';
/**
 * The learner's terminal (ttyS0) and the command-done marker (ttyS2).
 *
 * Ported from the `attachTerminal` half of spike/web/main.js. Two details
 * are the whole of it, and both are measured rather than assumed:
 *
 *  - v86 delivers serial output one byte per event, and a multi-byte
 *    character arrives in pieces, so the bytes are decoded by a *streaming*
 *    TextDecoder and handed over once per animation frame. One xterm write
 *    per frame, never one per byte.
 *  - a serial line has no window size, so resizing is `stty` over the exec
 *    channel, which also delivers SIGWINCH to the shell.
 */
const encoder = new TextEncoder();
/**
 * Batch output on the frame clock where there is one, and on a timer where
 * there is not: the headless state builder and the benchmark drive this same
 * code under Node, which has no rAF.
 */
const scheduleFlush = typeof requestAnimationFrame === 'function' ? (cb) => void requestAnimationFrame(() => cb()) : (cb) => void setTimeout(cb, 16);
export class GuestTerminal {
    emulator;
    channel;
    /** At most one attachment: attaching again detaches the previous one. */
    attachment = null;
    decoder = new TextDecoder();
    pending = [];
    scheduled = false;
    constructor(emulator, channel) {
        this.emulator = emulator;
        this.channel = channel;
        // One listener for the life of the VM. Detaching clears the attachment
        // rather than unregistering, so a second attach cannot leave two
        // listeners racing over the same decoder.
        emulator.add_listener(serialByteEvent(SERIAL_TERMINAL), (byte) => this.onByte(byte));
    }
    onByte(byte) {
        if (!this.attachment)
            return; // nobody is watching; the guest's shell carries on
        this.pending.push(byte);
        if (this.scheduled)
            return;
        this.scheduled = true;
        scheduleFlush(() => this.flush());
    }
    flush() {
        this.scheduled = false;
        const bytes = Uint8Array.from(this.pending);
        this.pending = [];
        const text = this.decoder.decode(bytes, { stream: true });
        if (text.length > 0)
            this.attachment?.onData(text);
    }
    attach(opts) {
        this.attachment = opts;
        this.pending = [];
        // A fresh decoder, because the previous attachment may have left half a
        // character behind.
        this.decoder = new TextDecoder();
        let attached = true;
        const stillMine = () => attached && this.attachment === opts;
        return {
            write: (data) => {
                if (!stillMine())
                    return;
                this.emulator.serial_send_bytes(SERIAL_TERMINAL, encoder.encode(data));
            },
            resize: (cols, rows) => {
                if (!stillMine() || !(cols > 0) || !(rows > 0))
                    return;
                // Fire and forget: it queues behind whatever the channel is doing,
                // and a wrongly sized terminal is not worth an error.
                void this.channel
                    .exec(`stty -F /dev/ttyS0 rows ${Math.floor(rows)} cols ${Math.floor(cols)}`, { timeoutMs: 5_000 })
                    .catch(() => undefined);
            },
            detach: () => {
                if (!stillMine())
                    return;
                attached = false;
                this.attachment = null;
                this.pending = [];
            },
        };
    }
}
/**
 * One byte on ttyS2 per finished interactive command, emitted by the
 * learner's `PROMPT_COMMAND`. This is the graph's refresh trigger, replacing
 * the server's 250 ms and 1200 ms guesses.
 *
 * The byte also says how the command ended: `o` when it exited zero and `e`
 * when it did not (sandbox/vm/bash_profile). That is what lets the tab tell a
 * learner who is working from one who is flailing — five errors in a row is
 * the sharpest stuck signal the product has, and the only one that fires for
 * someone who never presses Check (web/src/state/useStuck.ts).
 *
 * Any other byte means "a command finished, and this guest does not say how".
 * An image built before the status existed sends `x`, and it still refreshes
 * the graph exactly as it always did; only the stuck signal is absent, which
 * is the right way round for a value nothing may depend on.
 */
export class CommandDone {
    listeners = new Set();
    constructor(emulator) {
        emulator.add_listener(serialByteEvent(SERIAL_COMMAND_DONE), (byte) => this.fire(status(byte)));
    }
    fire(ok) {
        // Not coalesced: a subscriber that runs a command of its own — the graph
        // refresh does — drops or queues overlapping refreshes itself. A copy,
        // so a callback may unsubscribe from inside itself.
        for (const cb of [...this.listeners])
            cb(ok);
    }
    subscribe(cb) {
        this.listeners.add(cb);
        return () => {
            this.listeners.delete(cb);
        };
    }
}
/** `o` succeeded, `e` failed, anything else is a guest that does not say. */
function status(byte) {
    if (byte === 0x6f)
        return true;
    if (byte === 0x65)
        return false;
    return undefined;
}
//# sourceMappingURL=terminal.js.map